'use client';

import { forwardRef } from 'react';
// InlineSelect sizes its input from the current value, which means a per-instance CSS variable on
// `style` plus slot `classNames` — both locked on the core-ui `Select`, and neither expressible as
// a theme variant since the width is dynamic. This base stays on Mantine's Select for that; every
// other Select call site should use `@highlight-cp/core-ui`.
// oxlint-disable-next-line no-restricted-imports
import { Select as MantineSelect } from '@mantine/core';
// oxlint-disable-next-line no-restricted-imports
import type { SelectProps as MantineSelectProps } from '@mantine/core';
import { CREATE_OPTION_VALUE, SELECT_TRANSITION_PROPS } from '@highlight-cp/core-ui';
import type { CreatableSelectProps } from '@highlight-cp/core-ui';

import { useCreatableOptions } from './use-creatable-options';

/**
 * Internal shared base for the Select compositions. Renders Mantine's Select
 * directly (so `classNames`/`style` stay available for the inline-width variant)
 * and re-implements the v6-compat `creatable` flow. Not exported from the barrel
 * — use `CreatableSelect` or `InlineSelect`.
 */
export type CreatableSelectBaseProps = MantineSelectProps & CreatableSelectProps;

export const CreatableSelectBase = forwardRef<HTMLInputElement, CreatableSelectBaseProps>(
  (props, ref) => {
    const {
      creatable,
      getCreateLabel,
      onCreate,
      comboboxProps,
      data,
      onChange,
      searchable,
      ...rest
    } = props;
    const { augmentedData, searchProps, resolveCreate } = useCreatableOptions({
      creatable,
      data,
      getCreateLabel,
      onCreate,
    });

    const handleChange: MantineSelectProps['onChange'] = (value, option) => {
      if (creatable && value === CREATE_OPTION_VALUE) {
        const createdValue = resolveCreate();
        onChange?.(createdValue, { value: createdValue, label: createdValue });
        return;
      }
      onChange?.(value, option);
    };

    return (
      <MantineSelect
        ref={ref}
        {...rest}
        data={augmentedData}
        onChange={handleChange}
        searchable={creatable || searchable}
        {...searchProps}
        inputWrapperOrder={['label', 'input', 'error', 'description']}
        comboboxProps={{ transitionProps: SELECT_TRANSITION_PROPS, ...comboboxProps }}
      />
    );
  },
);
CreatableSelectBase.displayName = 'CreatableSelectBase';
